<!-- eslint-disable vue/no-v-html -->
<template>
  <div id="view" :class="{ receipt: isReceipt }">
    <div v-if="trip && !isUnauth">
      <ViewHeader :trip="trip" @error="emit('error', $event)" />
      <div class="panels">
        <Itineraries v-bind="$attrs" v-model:is-reload-itineraries="isReloadItineraries" v-model:itinerary-index="itineraryIndex" class="panel-left-flex" :class="{ on: isItineraries }" :trip-id="tripId" :trip="trip" @print-itinerary="printItinerary" @print-receipt="printReceipt" />
        <Support v-bind="$attrs" v-model:is-reload-messages="isReloadMessages" v-model:itinerary-index="itineraryIndex" class="panel-right-40-percent" :class="{ on: !isItineraries }" :trip-id="tripId" :trip="trip" />
      </div>
      <div v-if="receiptHtml" id="receipt">
        <div v-html="receiptHtml" />
      </div>
    </div>
    <div v-if="!trip && !isUnauth" class="loading" />
    <div v-if="isUnauth" class="unauth">
      <h3>Sorry, you do not have permission to view this trip.</h3>
      <p>If you believe you should have access, please contact your organization administrator to request access.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import ViewHeader from "../components/TripViewHeader.vue";
import Itineraries from "../components/common/ItineraryList.vue";
import Support from "../components/common/SupportMessages.vue";
import useApi from "../utils/apiCalls.js";

// Props and emit
const emit = defineEmits(["error"]);
const route = useRoute();
const router = useRouter();

// States
const es = ref(null);
const isUnauth = ref(false);
const isItineraries = ref(false);
const isReceipt = ref(false);
const isReloadItineraries = ref(false);
const isReloadMessages = ref(false);
const itineraryIndex = ref(route.params.itineraryIndex);
const receiptHtml = ref(null);
const trip = ref(null);
const tripId = ref(route.params.tripId);

const { apiGetRequest } = useApi(emit);

// Watchers – bidirectional sync between itineraryIndex ref and route param.
// flush:'sync' ensures the guard flag is checked immediately when the other watcher triggers.
let isSyncingRoute = false;

watch(itineraryIndex, val => {
  if (isSyncingRoute) return;
  if (String(val) !== String(route.params.itineraryIndex)) {
    router.replace({
      name: "trip_itinerary",
      params: { tripId: tripId.value, itineraryIndex: val },
    });
  }
  whitelabel();
}, { flush: "sync" });

watch(
  () => route.params.itineraryIndex,
  val => {
    if (String(val) === String(itineraryIndex.value)) return;
    isSyncingRoute = true;
    itineraryIndex.value = val;
    isSyncingRoute = false;
  }
);

watch(tripId, val => {
  router.push({ name: "trip", params: { tripId: val } });
});

// Lifecycle hooks
onMounted(() => {
  document.title = `Flightfox - Trip ${tripId.value}`;
  load();
  whitelabel();
});

onBeforeUnmount(() => {
  if (es.value) {
    es.value.close();
  }
});

// Methods
const whitelabel = () => {
  if (trip.value && trip.value.organization_logo) {
    const elements = document.querySelectorAll(".white-label-off");
    elements.forEach(element => {
      element.classList.remove("white-label-off");
      element.classList.add("white-label");
    });
  }
};

const load = () => {
  apiGetRequest({
    path: `/trips/${tripId.value}`,
    onSuccess: data => {
      isUnauth.value = true;
      trip.value = data;

      if (trip.value.id) {
        isUnauth.value = false;
      }

      if (!tripId.value) {
        tripId.value = trip.value.id;
      }

      subscribe();

      if (trip.value.organization_logo) {
        //if (import.meta.env.MODE.includes("development"))
        trip.value.organization_logo = trip.value.organization_logo.replace("/assets/", "/images/");

        document.querySelector("#logo img").src = trip.value.organization_logo;
      }

      if (route.name == "receipt") {
        printReceipt();
      }
    },
    onError: error => {
      if (error.response && error.response.status == 401) {
        isUnauth.value = true;
      } else {
        emit("error", new Error("Sorry, you cannot access this trip. Please contact Flightfox or create a new trip below."));
      }
    },
  });
};

const print = () => {
  nextTick(() => {
    window.print();
  });
};

const printItinerary = () => {
  whitelabel();
  isReceipt.value = false;
  print();
};

const printReceipt = () => {
  whitelabel();
  isReceipt.value = true;
  apiGetRequest({
    path: `/trips/${tripId.value}/receipt`,
    onSuccess: data => {
      if (trip.value.organization_logo) {
        data.html = data.html.replace(/white-label-off/g, `white-label`);
      }
      receiptHtml.value = data.html;
      print();
    },
    onError: error => {
      emit("error", error);
    },
  });
};

const subscribe = () => {
  if (window.EventSource !== undefined) {
    const url = `${import.meta.env.VITE_APP_FLIGHTFOX_API_URL}/trips/${tripId.value}/subscribe`;
    es.value = new EventSource(url, { withCredentials: false });

    es.value.addEventListener(
      "message",
      event => {
        const eventName = event.data;
        switch (eventName) {
          case "trip_changed_itineraries":
            isReloadItineraries.value = true;
            break;
          case "trip_changed_messages":
            isReloadMessages.value = true;
            break;
          case "trip_changed_expert":
          case "trip_changed_expiry":
            // Do nothing - skip warning
            break;
          default:
            console.log("Event received: UNKNOWN - ", eventName);
        }
      },
      false
    );

    es.value.addEventListener(
      "error",
      event => {
        if (event.readyState == EventSource.CLOSED) {
          console.log("Event was closed");
        }
      },
      false
    );
  }
};
</script>

<style lang="less">
#view {
  .unauth {
    margin: auto;
    max-width: 30rem;
    padding: 4rem;
    text-align: center;

    p {
      padding: 2rem 4rem;
    }
  }
}

@media print {
  * {
    -webkit-print-color-adjust: exact !important; /*Chrome, Safari */
    print-color-adjust: exact !important; /*Firefox*/
  }

  @page {
    margin: 1rem; /* needed to hide header and footer */
  }

  header .button-group {
    display: none !important;
  }

  #menu {
    display: none !important;
  }

  #logo {
    margin-bottom: 0 !important;
  }

  #view {
    #itineraries > h3,
    #itineraries .total,
    #itineraries .links,
    #itineraries .toggle,
    #itineraries .tabs,
    #support,
    .noprint {
      display: none !important;
    }

    #itineraries .avatar {
      display: inline-block;
      margin-right: 1rem;
    }

    #itineraries .route {
      display: inline-block;
    }

    #itineraries .details {
      display: block;
    }

    .card,
    .white-label {
      display: none !important;
    }

    #itineraries,
    .etickets {
      display: block !important;
    }

    .item {
      page-break-inside: avoid;
    }

    .identifier,
    .confirmations,
    .retail {
      color: @dark !important; // For printing, sometimes too light (Jop)
    }
  }

  #view.receipt {
    #receipt {
      display: block !important;
    }

    .header {
      display: none !important;
    }

    #itineraries {
      display: none !important;
    }

    table {
      table-layout: auto;

      td {
        border: none;
      }
    }
  }
}
</style>
